import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { StrategyKind } from '@termix/shared';

export type Lang = 'en' | 'ru';

const dict = {
  en: {
    brandSub: 'WEEX Futures + Spot',
    live: 'live',
    offline: 'offline',
    idle: 'idle',
    plan: 'plan',
    planNow: 'Your plan',
    planBtnFree: 'Free',
    planTabsCaption: 'Free 2 · Pro 5 · Desk 10 tabs',
    planTabsHint: 'How many chart tabs each plan allows',
    volumeUnlockHint: 'Volume discount 50%: ≥$150k → Pro tier · ≥$300k → Desk tier (API rebate stays)',
    volumeUnlockProgress: (vol: number, nextAt: number | null, nextTabs: number | null) => {
      const fmt = (n: number) => n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n));
      if (nextAt == null || nextTabs == null) return `Traded $${fmt(vol)} · volume discount already at max`;
      return `Traded $${fmt(vol)} · $${fmt(nextAt)} → next 50% discount tier (${nextTabs} tabs)`;
    },
    tabs: 'tabs',
    help: 'Help',
    chartFocus: 'Chart',
    chartFocusOff: 'Panels',
    cabinet: 'Cabinet',
    admin: 'Admin',
    logout: 'Log out',
    stopStrategies: 'Stop AI Chips',
    stopStrategiesTitle: 'Stops every AI Chip on every tab. Does not close exchange positions or orders.',
    stopStrategiesConfirm: 'Stop all AI Chips on every tab?\n\nExchange positions and orders stay open — only bots stop.',
    stopStrategiesDone: 'All AI Chips stopped (positions on the exchange were not closed)',
    pro: 'Pro $10',
    desk: 'Desk $20',
    helpVideoShare: 'Share video link',
    helpVideoShareCopied: 'Link copied',
    helpVideoShareHint: 'External link to the overview — open, watch, then register for the desk.',
    helpVideoRegister: 'Register / log in →',
    helpVideoDirect: 'Direct file',
    addTab: '+ tab',
    closeTab: 'Close tab',
    pair: 'Pair · Futures / Spot',
    filter: 'filter…',
    otherPair: 'Other…',
    changePair: 'Change pair',
    changePairTitle: 'Load this pair on the current tab (strategy range will reset)',
    pairSort: 'Sort pairs',
    pairSortCol: 'Click to sort · click again to flip',
    pairSuperFilter: 'Super filter',
    pairFilterUse: 'Include this range in the filter',
    pairRangeFrom: 'from',
    pairRangeTo: 'to',
    pairRangeHint: 'Tick a box to apply that range',
    pairVolHint: 'Quote volume · use K / M / B',
    pairOiHint: 'Open interest (contracts) · use K / M / B',
    pairLoading: 'Loading tickers…',
    pairRsiLoading: 'Computing RSI…',
    pairOiLoading: 'Loading open interest…',
    pairEmpty: 'No pairs match',
    adminUpdateTitle: 'Termix update',
    adminUpdateAvailable: 'New update',
    adminUpdateCurrent: 'Up to date',
    adminUpdateWhatsNew: 'What’s in this update',
    adminUpdateHistory: 'Update history',
    adminUpdateHistoryEmpty: 'No git history yet',
    adminUpdateHistoryExpand: 'Show commit details',
    adminUpdateHistoryNoBody: 'No extended commit message',
    adminUpdateCheck: 'Check for updates',
    adminUpdateInstall: 'Install update',
    adminUpdateInstalling: 'Installing…',
    adminUpdateNoNotes: 'No commit notes between these versions',
    tfTitle: (tf: string) => `Candle timeframe ${tf}`,
    drawing: 'DRAWING',
    toolCursor: '↖ Cursor',
    toolHline: '― H-line',
    toolRange: '▣ Range',
    toolTrend: '/ Trend',
    toolRuler: '⌞ Ruler',
    toolEraser: '✕ Eraser',
    tipCursor: 'Pan/zoom. Drag trend points; range corners resize both axes, edges move price or time only.',
    tipHline: 'Click a price to place a horizontal level.',
    tipRange: 'Two clicks: top/bottom of the strategy box. “By time” adds a time window (future allowed).',
    tipTrend: 'Two clicks for a trend line, then drag the round handles.',
    tipRuler: 'Press and drag to measure price Δ, %, and time (like TradingView). Esc clears.',
    tipEraser: 'Click a drawing or an indicator (SMA / EMA / RSI / Volume / levels / trends / OB) to remove or turn it off. Strategy box clears too.',
    color: 'color',
    jumpToLive: 'Go to realtime',
    jumpToLiveTitle: 'Scroll to current price with ~10% empty future (like TradingView)',
    byTime: 'by time',
    byTimeTitle: 'On: box limited by time (can extend into the future). Off: price band only across history.',
    saveChart: 'Save chart',
    saved: 'Saved',
    clearRange: 'Clear range',
    clearRangeTitle: 'Remove the strategy work box from this tab',
    toStrategy: '→ AI Chip',
    boxHint: 'Range = «▣» · 2 clicks',
    order: 'Order',
    strategies: 'AI Chip',
    indicators: 'Indicators',
    size: 'Size',
    price: 'Price',
    stopPrice: 'Stop',
    needKey: 'API key required',
    positions: 'Positions',
    orders: 'Orders',
    trades: 'Trades',
    depth: 'Book',
    balance: 'Balance',
    bookDom: 'DOM',
    bookLadder: 'Ladder',
    bookTape: 'Tape',
    bookDensity: 'density',
    bookClickSoon: 'Click → Limit at this price (Order ticket)',
    bookClickHint: 'Click a DOM / ladder / tape price to arm Limit on the Order tab',
    bookArmedLimit: (side: string, price: string) => `Limit ${side} @ ${price} — check Size, then send`,
    termExpandPanel: 'Panel ↓',
    termExpandPanelTitle: 'Expand positions / book — covers the chart',
    termExpandChart: 'Chart ↑',
    termExpandChartTitle: 'Expand chart — covers the basement',
    termSplit: 'Split',
    termSplitTitle: 'Show chart and basement together',
    termChartCovered: 'Chart hidden — expand it above',
    // legacy aliases kept for any leftover refs
    termExpand: 'Panel ↓',
    termCollapse: 'Split',
    termExpandTitle: 'Expand positions / book — covers the chart',
    termCollapseTitle: 'Show chart and basement together',
    termHide: 'Chart ↑',
    termShow: 'Panel ↓',
    termHideTitle: 'Expand chart — covers the basement',
    termShowTitle: 'Expand positions / book — covers the chart',
    loadTape: 'Press Refresh to load the tape',
    showPanels: 'Show panels',
    showPositions: 'Show positions ↓',
    lang: 'EN',
    langMenu: 'Language',
    langEn: 'English',
    langRu: 'Русский',
    strategiesTitle: 'AI CHIP',
    strategiesHint: 'Pick an AI Chip (Pulse / Snap / Ladder / Book) → draw a range → set risk → Start. Same API key can run chips on different pairs; same pair stays one live bot.',
    chipPulseName: 'AI Chip · Pulse',
    chipPulseShort: 'Momentum chip — reads the range and fires dense long/short setups',
    chipPulseDesc:
      'AI Chip · Pulse watches the box in real time: breakout / pullback on each side. The chart sketch updates instantly; Start runs the same chip live. Short dial stays stricter.',
    chipSnapName: 'AI Chip · Snap',
    chipSnapShort: 'Mean-reversion chip — snaps entries at SMA/RSI extremes (+ fractal)',
    chipSnapDesc:
      'AI Chip · Snap waits for deep pullbacks vs SMA + RSI. Dial “more” relaxes depth; Fractal wings (0=off … 5=strict) confirm pivots. Fewer trades than Pulse — at extremes.',
    chipLadderName: 'AI Chip · Ladder',
    chipLadderShort: 'Grid chip — laddered averaging with TP on each leg',
    chipLadderDesc:
      'AI Chip · Ladder builds a DCA ladder from the arm price. Long steps down, short steps up; each leg has its own TP. Same desk dials as Pulse / Snap.',
    chipBookName: 'AI Chip · Book',
    chipBookShort: 'Order-block chip — same OB boxes as on the chart',
    chipBookDesc:
      'AI Chip · Book trades the OB boxes on the chart: enter on tap/approach (nearer brick wins — not a far wick under red supply), stop = far-edge bust, take = holdBars / opposing brick. Density = pickiness + how near.',
    bookWatchEnter: (side: string, lo: string, hi: string, str: string) =>
      `Book · ENTER ${side} · brick ${lo}–${hi} · str ${str}`,
    bookWatchWait: (side: string, lo: string, hi: string, dist: string, str: string) =>
      `Book · wait ${side} · brick ${lo}–${hi} · ${dist}% away · str ${str}`,
    bookWatchNone: (fresh: number, tested: number) =>
      `Book · no brick in range yet (fresh ${fresh} · tested ${tested}) — draw range / loosen density`,
    stratRunning: 'Running',
    stratStopped: 'Stopped',
    stratStoppedLeft: 'Stopped · left position',
    stratStoppedClosed: 'Stopped · closed position',
    stratSoftStopping: 'Soft stop · exit when min profit ok',
    stopStrategySoftActive: '⏹ Soft stop active…',
    strategyStartLine: 'Strategy start — compare REAL fills vs sketch from this line',
    posGroupByTab: 'this tab',
    posGroupByTabTitle: 'On: only positions / orders / trades for the open chart tab. Off: all symbols.',
    posGroupByTabEmpty: 'No positions on this tab',
    ordersGroupByTabEmpty: 'No orders on this tab',
    tradesGroupByTabEmpty: 'No trades on this tab',
    posJumpTab: 'Open this symbol’s tab',
    stratDialNeedStart: 'Dials only tune the chip — press ▶ Start AI Chip (tab must show live).',
    stratInPosition: 'In position',
    stratSignals: 'Signals only',
    stratStatusIdle: 'No AI Chip on this tab',
    stratTabLive: 'live',
    rangeTimeEnding: 'Strategy time ending soon — drag the range right to extend',
    rangeTimeEndingLeft: (s: string) => `Strategy window ends in ${s} — drag right edge to extend`,
    rangeLabel: 'Range',
    rangeNone: 'not set — full chart',
    rangeTrades: 'Trades in range',
    rangeDrawHint: 'Draw «▣ Range» — trades for the selected AI Chip appear on the chart.',
    rangeSketchEmpty: 'Range is set, but this AI Chip found no sketch trades here — try Pulse, relax density / fractal, or widen the box.',
    riskPerOrder: 'Risk per order · % of deposit',
    riskMax: 'Max strategy load · % of deposit',
    sideLong: 'Long',
    sideShort: 'Short',
    tradesFewer: 'fewer',
    tradesMore: 'more',
    shortStricterHint: 'Short dial starts stricter — fewer weak shorts by default.',
    fractalWings: 'Fractal wings',
    fractalOff: 'off',
    fractalWingsHint: '0 = no fractal confirm · 1–5 = Williams wings (higher = fewer entries). Affects sketch + live.',
    maxClips: 'Max clips (entry + averaging)',
    onlyProfit: 'Only profit · min profit %',
    onlyProfitHint: 'If already in profit — wait for at least this net % (after fees). If the soft exit fires while flat/red — close anyway (no sticking in a loser). Hard range stop ignores this.',
    expandCollapse: 'Collapse ↑',
    monitoringBusy: 'Refreshing…',
    needKeyStrategy: 'Connect an API key (Cabinet) before starting.',
    startStrategy: '▶ Start AI Chip',
    stopStrategy: '⏹ Stop AI Chip',
    sizeWarnMinLot: (minQty: number, notional: number, riskNotional: number, sym: string) =>
      `Risk clip (~${riskNotional.toFixed(2)} USDT) is below ${sym} min lot ${minQty} — order will use min (~${notional.toFixed(2)} USDT).`,
    sizePreview: (qty: number, notional: number, step: number) =>
      `Clip ≈ ${qty} (lot step ${step}) · ~${notional.toFixed(2)} USDT`,
    delistNoticeTitle: 'Delisting',
    killDone: (msg: string) => msg,
    undo: '↶ Undo',
    redo: '↷ Redo',
    undoTitle: 'Undo (Ctrl+Z) — drawings, range, pair, timeframe · up to 10',
    redoTitle: 'Redo (Ctrl+Shift+Z) — up to 10 forward',
    margin: 'Margin',
    marginExchange: 'On exchange',
    marginApply: 'Apply on exchange',
    marginAvailable: 'Available margin',
    marginUsed: 'Used margin',
    marginMaint: 'Maint. margin',
    marginEquity: 'Equity',
    marginEffLev: 'Position leverage',
    marginRatio: 'Margin ratio',
    cross: 'Cross',
    isolated: 'Isolated',
    leverage: 'Leverage',
    barCountdown: 'Bar close timer',
    barCountdownHint: 'Countdown under the last price on the right scale — ticking means the desk is alive.',
    indicatorsTitle: 'CHART INDICATORS',
    indicatorsHintOnly: 'Visual hints only — they never arm or steer AI Chips.',
    indSectionBasics: 'BASICS',
    indSectionZones: 'OVERBOUGHT / OVERSOLD',
    indSectionStructure: 'LEVELS · TRENDS · ORDER BLOCKS',
    indZonesHint: 'Guide bands on the RSI pane. Not a trade signal by itself.',
    indStructureHint: 'Auto drawings from swings — read as context, erase or ignore anytime.',
    indRsiZones: 'RSI zones',
    indRsiOb: 'Overbought',
    indRsiOs: 'Oversold',
    indAutoLevels: 'Auto levels',
    indAutoLevelsHint: 'Axis label ×N = how many times price touched that level (not an order-block id).',
    indLevelsCount: 'Levels count',
    indAutoTrends: 'Auto trends',
    indTrendsCount: 'Trend lines per side',
    indOrderBlocks: 'Order blocks',
    indBlocksCount: 'How many',
    indOrderBlocksHint: 'Swing→BOS volumetric OBs (LuxAlgo-style). Metrics = interval vol + share%. Book polled while on.',
    indObBookMode: 'OB width',
    indObBookAnnotate: 'Body',
    indObBookWiden: 'Book walls',
    indObBookModeHint: 'Body = narrow on the candle. Walls = wider from the live book.',
    indObBreakers: 'Breakers',
    indObBreakersCount: 'Breaker count',
    indObBreakersHint: 'Mitigated OBs with flipped polarity (broken demand → resistance, supply → support). Dashed ghost zones.',
    indSensitivity: 'Structure sensitivity',
    indSensitivityHint: 'Strict ← fewer / stronger · loose → more / softer. Shared by levels, trends, OB, breakers.',
    chipTagPulse: 'Pulse',
    chipTagSnap: 'Snap',
    chipTagLadder: 'Ladder',
    chipTagBook: 'Book',
    posChipCol: 'Chip',
    posChipEmpty: '',
    posChipTitle: 'AI Chip running on this symbol (empty = manual / no chip)',
    indSensitivityStrict: 'strict',
    indSensitivityLoose: 'loose',
    indSaveStructure: 'Save and bind objects to timeframe',
    indSaveStructureHint: 'Pin current hints on the chart with a TF tag. Erase later with the eraser.',
    indSaveStructureDone: (n: number) => `Bound ${n} objects to timeframe`,
    indRsiPane: 'RSI placement',
    indVolPane: 'Volume placement',
    indPaneSeparate: 'Separate pane',
    indPaneMain: 'On main chart',
    indPaneDragHint: 'Drag the RSI / Volume chip: drop on the upper half → main chart; lower half → basement pane (hides when both are on main).',
    indEraseChip: 'eraser · click to disable',
    sizeModeCoins: 'Coins',
    sizeModeUsdt: 'USDT',
    sizeModePct: '% deposit',
    sizeHintCoins: 'Order size in base coins.',
    sizeHintUsdt: 'Notional in USDT → converted to coins at mark / last.',
    sizeHintPct: '% of available margin → coins at mark / last.',
    sizeInvalid: 'Enter a valid size (coins, USDT, or % of deposit)',
    bookPrice: 'Price',
    bookQty: 'Qty',
    bookCum: 'Cum',
    themeDark: 'Dark',
    themeIvory: 'Ivory',
    themeToggle: 'Theme',
    themeToggleTitle: 'Switch dark / ivory (bone) desk',
    tgTitle: 'Telegram',
    tgHint: 'Enter your @handle — we give a bot deep-link. Alerts / bind come next.',
    tgPlaceholder: '@username',
    tgSave: 'Save handle',
    tgOpenBot: 'Open bot',
    tgSaved: (h: string) => `Telegram ${h} saved`,
    tgNeedHandle: 'Enter a Telegram @username first',
    tgBotMissing: 'Bot username is not configured yet — handle is saved for later bind.',
    indHistoryScrollHint: 'Pan the chart left to load older bars (≥15m). 1m / 5m stay short.',
    indHistoryLoading: 'Loading older bars…',
    indHistoryDone: (n: number) => `History · ${n} candles`,
    indHistoryFail: (m: string) => `History load failed: ${m}`,
    noData: 'No data',
    bar: 'bar',
    last: 'last',
    tradesOnChart: 'trades',
    rangeBox: 'Strategy range',
    eraseHint: 'delete',
    object: 'object',
    draftPoint: 'Start point',
    refresh: 'Refresh',
    noKeyShort: 'no key',
    connectKeyPositions: 'Connect a WEEX key on the right to see positions',
    connectKeyOrders: 'Connect a key to see orders',
    connectKeyTrades: 'Connect a key — your closed trades appear here (not the public tape)',
    noPositions: 'No open positions',
    posSelectHint: 'Click a row or chart avg → close % · TP/SL',
    posToolsTitle: 'Position',
    posClosePct: 'Close %',
    posCloseBtn: 'Close',
    posCloseHint: 'Market reduce-only',
    posAttachProtective: 'Set',
    posProtectiveHint: 'Reduce-only TP/SL on the open size',
    posMoreProtective: 'TP / SL',
    posLessProtective: 'Hide',
    posDismiss: 'Close panel',
    posBusy: '…',
    posSize: 'Size',
    posEntry: 'Entry',
    posUpnl: 'uPnL',
    posQtyClose: 'Close qty',
    posClosed: (pct: number, sym: string) => `Closed ${pct}% of ${sym}`,
    posProtectiveAttached: (sym: string) => `TP/SL set on ${sym}`,
    posNeedProtective: 'Turn on TP, SL or trailing first',
    protectiveTitle: 'TP / SL / Trailing',
    protectiveHint: 'Placed with the entry (reduce-only), like a WEEX bracket.',
    protectiveTp: 'Take profit',
    protectiveSl: 'Stop loss',
    protectiveTrail: 'Trailing',
    protectiveOff: 'Off',
    protectivePct: '% from entry',
    protectivePrice: 'Price',
    protectiveCallback: 'Callback %',
    protectiveActivate: 'Activate at (optional)',
    protectivePreview: 'Approx. from entry',
    protectiveTpPnl: 'At TP',
    protectiveSlPnl: 'At SL',
    noOrders: 'No open orders',
    noClosedTrades: 'No real fills on this pair yet',
    noBalance: 'No balance data',
    loadBook: 'Press Refresh to load the order book',
    tabAdded: 'Tab added',
    tabClosed: 'Tab closed',
    lastTab: 'Cannot close the last tab',
    pairExample: 'Pair: e.g. BTCUSDT',
    badPair: (sym: string) => `Pair «${sym || '?'}» is not a valid WEEX Futures/Spot symbol — pick another`,
    pairReset: (sym: string) => `Pair: ${sym} · market reset (range cleared)`,
    rangeApplied: (r: string) => `Range applied to live strategy · ${r}`,
    rangeTab: (r: string) => `Tab range: ${r || 'price'}`,
    rangeClearedLive: 'Range cleared · strategy runs without a box',
    rangeCleared: 'Strategy range cleared',
    chartSaved: (sym: string) => `Chart ${sym} saved`,
    timeOff: 'Time window off — price band only',
    timeRestored: 'Time window restored',
    needKeyCabinet: 'Connect a WEEX API key in Cabinet first',
    strategyStarted: (k: string, risk: string, max: string, box: string) =>
      `${k} started · risk ${risk}%/order · max ${max}%${box}`,
    strategyStopped: 'Strategy stopped',
    strategyStoppedLeft: 'Strategy stopped · position left open (unsupervised)',
    strategyStoppedClosed: 'Strategy stopped · position closed on exchange',
    strategySoftStopped: 'Soft stop · no entries/adds; closes now if min profit is met (else waits)',
    strategySoftActive: 'Soft stop — no entries/adds; market-closes as soon as only-profit allows (all chips)',
    stopDialogTitle: 'Stop AI Chip',
    stopDialogHint: 'Choose how to handle the open position and pending entries.',
    stopModeClose: 'Stop and close position',
    stopModeCloseHint: 'Kill the bot and market-close the open position now (reduce-only).',
    stopModeLeave: 'Stop, leave position',
    stopModeLeaveHint: 'Bot stops immediately. Position stays on the exchange without the strategy.',
    stopModeSoft: 'Soft stop (min profit → close)',
    stopModeSoftHint: 'No new entries/adds. If already at min profit (or only-profit off) — market close now. If still below min while green — wait. Hard range TP/SL still apply. Same for Pulse / Snap / Ladder.',
    keyConnected: (alias: string) => `Key «${alias}» connected`,
    keyConnectedBal: (alias: string, equity: number, available: number) =>
      `Key «${alias}» connected · equity ${equity.toFixed(2)} · available ${available.toFixed(2)} USDT`,
    keySavedIpBlocked: (alias: string, ip: string) =>
      ip
        ? `Key «${alias}» saved. Balance/orders go over IPv4 ${ip} (charts stay on IPv6). Whitelist that IPv4, update desk, then Monitoring.`
        : `Key «${alias}» saved. Private WEEX calls use IPv4 only; charts keep IPv6. Update desk, then Monitoring.`,
    accountRefreshed: (equity: number, available: number) =>
      `Balance updated · equity ${equity.toFixed(2)} · available ${available.toFixed(2)} USDT`,
    accountRefreshFail: 'Balance refresh failed',
    accountRefreshHint: 'Press Monitoring to load equity / margin from WEEX',
    orderSent: (type: string, side: string, qty: string, sym: string) => `${type} ${side} ${qty} ${sym} sent`,
    planOn: (id: string) => `Plan ${id} activated`,
    marginApplied: (mode: string, lev: string) => `Margin ${mode} · ${lev}x applied on exchange`,
    fewCandles: (sym: string, tf: string) => `Too few candles for ${sym} ${tf}`,
    loadingTf: 'Loading candles…',
    netStable: 'Market stable',
    netWeak: 'Market weak — retrying…',
    netOffline: 'Market offline',
    netConnecting: 'Market connecting…',
    accountOk: 'account OK',
    accountEmpty: 'balance 0 / check key',
    accountFail: 'account error',
    personalCabinet: 'PERSONAL CABINET',
    tabLabel: 'Tab',
    keyOk: 'key OK',
    noKey: 'no key',
    positionsCount: 'Positions',
    perMonth: '/mo',
    freePlan: 'no fee',
    monitoring: 'Monitoring',
    monitoringOpened: 'Monitoring open · balance & positions refreshed',
    monitorEquityChart: 'Futures equity',
    monitorSpotBalance: 'Spot USDT',
    monitorPnlChart: 'Realized PnL',
    monitorDeposit: 'Deposit',
    monitorChartsEmpty: 'Waiting for samples…',
    monitorChartsHint: 'Spot and Futures are separate wallets — each has its own equity chart on this device',
    monitorMarketFutures: 'Futures',
    monitorMarketSpot: 'Spot',
    monitorEquitySeparate: 'own wallet · own chart',
    monitorSpotAvailable: 'Available',
    monitorAvailShort: 'avail',
    monitorChartFor: 'Equity chart',
    monitorSwitchMarketHint: 'Switch tab market — Spot and Futures equity charts are stored separately',
    monitorPeriod15m: '15m',
    monitorPeriod1h: '1h',
    monitorPeriod6h: '6h',
    monitorPeriod24h: '24h',
    monitorPeriodAll: 'All',
    monitorMeta: (pts: number, period: string) => `${pts} pts · ${period}`,
    monitorPollEvery: 'every 12s',
    expand: 'Expand ↓',
    apiKeysOne: 'API keys (shared across tabs)',
    keyShareHint: 'One key works on every tab. Start strategies on different pairs with the same key; same pair stays one live bot. Margin below covers all positions on this key.',
    liveBotsOther: 'Other live bots on this key',
    noKeyOption: '— no key —',
    keyNotConnected: 'No key connected yet',
    addKey: '+ Add / replace key',
    connectKey: 'Connect API key',
    deleteKey: 'Delete',
    deleteKeyConfirm: (alias: string) => `Delete key «${alias}» from Termix? Exchange account is not touched.`,
    keyDeleted: (alias: string) => `Key «${alias}» removed`,
    keySaving: 'Saving…',
    keySavedList: 'Saved keys — pick one for this tab, or delete',
    keyReplaceHint: 'Re-using the same name (e.g. main) replaces that key. Secrets are not shown again — delete and add anew.',
    cancel: 'Cancel',
    save: 'Save',
    billingHint: 'Plans: Free / Pro $10 / Desk $20 — Aptos USDT in Cabinet. Or 50% off by volume (≥$150k / ≥$300k).',
    account: 'Account',
    depositWallet: 'Aptos deposit wallet',
    depositWalletPending: 'Wallet pending — Admin → Install update (billing migrate), then reopen this dialog',
    depositWalletHint: 'Send native Aptos USDT here to pay for Pro/Desk. Platform fee goes to treasury on settle.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    savePassword: 'Save password',
    passwordChanged: 'Password updated',
    tabLimitPay: 'Tab limit — upgrade to open more desks',
    upgradeTitle: 'Upgrade plan',
    upgradeHint: (plan: string, amount: number) => `Plan ${plan}: send ${amount} USDT (Aptos) to your deposit address.`,
    platformFee: 'Platform fee',
    iPaid: 'Check & activate',
    closedTradesHint: 'REAL fills stay on this pair even after you clear the range',
    saveWithChart: 'Save with chart',
    on: 'on',
    helpTitle: 'Termix · knowledge base',
    helpBack: '← to desk',
    helpBackLogin: '← to login / register',
    helpVideoTitle: 'Video overview · ~3 min',
    helpVideoLead: 'Full desk tour: theme & language · Panel/Chart expand · book→Limit · OB body/walls · levels · Pulse/Snap Range · eraser square. Cursor + captions.',
    authTagline: 'WEEX desk · AI Chip · Pulse / Snap / Ladder',
    authHelpLink: 'Desk overview (~3 min):',
    authPlansLine: 'Free 2 · Pro $10 / 5 tabs · Desk $20 / 10 tabs',
    authEmail: 'Email',
    authPassword: 'Password',
    authPasswordPh: 'at least 8 characters',
    authLogin: 'Log in',
    authRegister: 'Create account',
    authSwitchRegister: 'No account — register',
    authSwitchLogin: 'Already have an account — log in',
    authAdminLink: 'Admin login →',
    authBadCreds: 'Wrong email or password. Admin panel: /admin. New users — register.',
    authWeexAsk: 'Do you already have a WEEX account?',
    authWeexYes: 'Yes',
    authWeexNo: 'Not yet',
    authWeexRefLead: 'Register on WEEX with our link — 10% cashback on all fees:',
    authWeexRefCta: 'Open WEEX registration →',
    weexRefUrl: 'https://www.weex.com/register?vipCode=myway1',
    helpQuickTitle: 'Quick start',
    helpQuick: [
      'Open a pair and timeframe (15m works well for Pulse).',
      'Draw a **Range** — the AI Chip work box.',
      'Right panel → **AI Chip** → **AI Chip · Pulse**.',
      'Set risk % → Cabinet → API key → **Start strategy**.',
    ],
    helpStratTitle: 'AI Chips · Termix main focus',
    helpStratLead: 'Three AI Chips run on the tab with your API key. Draw a range → pick Pulse / Snap / Ladder → risk % → Start. The chart immediately sketches that chip’s trades.',
    helpVolTitle: 'AI Chip · Pulse ★',
    helpVolBody: 'Momentum chip: breakout beyond lookback high/low or pullback to mid-range. On an active market it produces **many clean trades** inside the box — L / S / xL / xS markers on the candles. Exits are gated by **only profit** (no close into a loss after fees). Main “working” chip for the desk.',
    helpHideepTitle: 'AI Chip · Snap',
    helpHideepBody: 'Mean-reversion chip: deep pullback from SMA + RSI. Fractal wings dial 0–5 (0=off). Fewer trades than Pulse — but at extremes.',
    helpDcaTitle: 'AI Chip · Ladder',
    helpDcaBody: 'Grid chip: laddered averaging with TP on each leg. Stays inside the trade range.',
    helpStratFoot: 'Switching chip writes to the tab automatically; Saved goes dirty if the drawing is not saved yet.',
    helpRangeTitle: 'Range and trades on the chart',
    helpRangeItems: [
      '**Range** — two clicks: high/low. The **by time** checkbox sits next to ▣ Range in one frame (then Trend and Eraser) — time binding is for that box; you can stretch into the future.',
      'While the box exists, the strategy and trade sketch run **only inside** it.',
      '**Trades in range** — markers of the selected algorithm on history (preview before arming).',
      'Eraser on the box clears the tab range; without a box the strategy watches the whole chart.',
    ],
    helpChartTitle: 'Chart and timeframes',
    helpChartBody: 'Buttons **15m / 30m / 1h / 4h** change the interval on the current tab. The top tab shows pair and TF together.',
    helpChartPair: '**Change pair** clears the old price box — prices from another coin break the scale.',
    helpDrawTitle: 'Drawing',
    helpDrawItems: [
      '**Cursor** — pan/zoom, drag trend points and range edges.',
      '**H-line** — level on click; drag the handle.',
      '**Trend** — two clicks, then round handles.',
      '**Eraser / Delete** — remove a drawing or turn off an indicator (SMA / RSI / levels / OB…). Undo / Redo on the drawing bar.',
      '**RSI / Volume chips** — drag onto the main chart or keep a separate pane (TradingView-style).',
    ],
    helpRiskTitle: 'Risk, TP / SL / Trailing',
    helpRiskBody1: '**Risk % per order** — share of equity on one entry. **Max % strategy** — cap on total exposure.',
    helpRiskBody2: 'Order tab → **Protective** on new entries. Open positions: click the row or the **avg** line/badge on the chart → close % slider + attach TP/SL/trailing to live size (reduce-only).',
    helpRiskBody3: '**Stop strategies** stops every bot on every tab. It does not close exchange positions or orders by itself — use Position tools for that.',
    helpBottomTitle: 'Bottom panel',
    helpBottomItems: [
      '**Positions / Orders** — for this tab’s key.',
      '**Trades** — your closed fills (not the public tape).',
      '**Book / Balance** — order book and margin from the exchange.',
    ],
    helpAdminTitle: 'Admin · updates',
    helpAdminBody: '“Check” / “Install” look at **GitHub main** of',
    helpAdminFoot: 'If status says up to date but the site looks old — Attach git + Install (key /root/.ssh/termix_deploy).',
  },
  ru: {
    brandSub: 'WEEX Futures + Spot',
    live: 'live',
    offline: 'offline',
    idle: 'idle',
    plan: 'план',
    planNow: 'Ваш план',
    planBtnFree: 'Free',
    planTabsCaption: 'Free 2 · Pro 5 · Desk 10 вкладок',
    planTabsHint: 'Сколько вкладок графика даёт каждый тариф',
    volumeUnlockHint: 'Скидка 50% по объёму: ≥$150k → тариф Pro · ≥$300k → Desk (API-ребейт как был)',
    volumeUnlockProgress: (vol: number, nextAt: number | null, nextTabs: number | null) => {
      const fmt = (n: number) => n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n));
      if (nextAt == null || nextTabs == null) return `Объём $${fmt(vol)} · скидка 50% по объёму уже на максимуме`;
      return `Объём $${fmt(vol)} · $${fmt(nextAt)} → следующий уровень скидки 50% (${nextTabs} вкладок)`;
    },
    tabs: 'вкладок',
    help: 'Справка',
    chartFocus: 'График',
    chartFocusOff: 'Панели',
    cabinet: 'Кабинет',
    admin: 'Admin',
    logout: 'Выйти',
    stopStrategies: 'Стоп AI-чипы',
    stopStrategiesTitle: 'Гасит все AI-чипы на всех вкладках. Позиции и ордера на бирже не закрывает.',
    stopStrategiesConfirm: 'Остановить все AI-чипы на всех вкладках?\n\nПозиции и ордера на бирже не трогаем — только ботов.',
    stopStrategiesDone: 'Все AI-чипы остановлены (позиции на бирже не закрывались)',
    pro: 'Pro $10',
    desk: 'Desk $20',
    helpVideoShare: 'Ссылка на видео',
    helpVideoShareCopied: 'Ссылка скопирована',
    helpVideoShareHint: 'Внешняя ссылка на обзор — открыть, посмотреть, потом регистрация на деск.',
    helpVideoRegister: 'Регистрация / вход →',
    helpVideoDirect: 'Прямой файл',
    addTab: '+ вкладка',
    closeTab: 'Закрыть вкладку',
    pair: 'Пара · Futures / Spot',
    filter: 'фильтр…',
    otherPair: 'Другая…',
    changePair: 'Сменить пару',
    changePairTitle: 'Загрузить выбранную пару на эту вкладку (диапазон стратегии сбросится)',
    pairSort: 'Сортировка пар',
    pairSortCol: 'Клик — сортировка · ещё раз — наоборот',
    pairSuperFilter: 'Суперфильтр',
    pairFilterUse: 'Учитывать этот диапазон',
    pairRangeFrom: 'от',
    pairRangeTo: 'до',
    pairRangeHint: 'Галочка включает условие',
    pairVolHint: 'Объём котировки · можно K / M / B',
    pairOiHint: 'Открытый интерес (контракты) · можно K / M / B',
    pairLoading: 'Гружу тикеры…',
    pairRsiLoading: 'Считаю RSI…',
    pairOiLoading: 'Гружу открытый интерес…',
    pairEmpty: 'Нет совпадений',
    adminUpdateTitle: 'Обновление Termix',
    adminUpdateAvailable: 'Новое обновление',
    adminUpdateCurrent: 'Актуально',
    adminUpdateWhatsNew: 'Что в этом обновлении',
    adminUpdateHistory: 'История обновлений',
    adminUpdateHistoryEmpty: 'История git пока пуста',
    adminUpdateHistoryExpand: 'Показать подробности коммита',
    adminUpdateHistoryNoBody: 'Развёрнутого описания в коммите нет',
    adminUpdateCheck: 'Проверить обновления',
    adminUpdateInstall: 'Установить обновление',
    adminUpdateInstalling: 'Ставится…',
    adminUpdateNoNotes: 'Нет списка коммитов между этими версиями',
    tfTitle: (tf: string) => `Таймфрейм свечей ${tf}`,
    drawing: 'РИСОВАНИЕ',
    toolCursor: '↖ Курсор',
    toolHline: '― H-линия',
    toolRange: '▣ Диапазон',
    toolTrend: '/ Тренд',
    toolRuler: '⌞ Линейка',
    toolEraser: '✕ Ластик',
    tipCursor: 'Просмотр графика. Точки тренда; углы диапазона — обе оси, рёбра — только цена или только время.',
    tipHline: 'Клик по цене — горизонтальный уровень.',
    tipRange: 'Два клика: верх/низ бокса. «По времени» добавляет окно (можно в будущее).',
    tipTrend: 'Два клика — трендовая. Потом двигайте круглые точки.',
    tipRuler: 'Зажми и тяни — Δ цены, %, время (как в TradingView). Esc сбрасывает.',
    tipEraser: 'Клик по рисунку или индикатору (SMA / EMA / RSI / Volume / уровни / тренды / OB) — снять или выключить. Диапазон стратегии тоже снимается.',
    color: 'цвет',
    jumpToLive: 'К текущему времени',
    jumpToLiveTitle: 'Прокрутить к цене с ~10% пустого будущего (как в TradingView)',
    byTime: 'по времени',
    byTimeTitle: 'Вкл: бокс ограничен по времени (можно в будущее). Выкл: только полоса цен.',
    saveChart: 'Сохранить график',
    saved: 'Сохранено',
    clearRange: 'Снять диапазон',
    clearRangeTitle: 'Снять рабочий бокс стратегии с вкладки',
    toStrategy: '→ AI Chip',
    boxHint: 'Диапазон = «▣» · 2 клика',
    order: 'Ордер',
    strategies: 'AI-чип',
    indicators: 'Индикаторы',
    size: 'Размер',
    price: 'Цена',
    stopPrice: 'Stop',
    needKey: 'Нужен API ключ',
    positions: 'Позиции',
    orders: 'Ордера',
    trades: 'Сделки',
    depth: 'Стакан',
    balance: 'Баланс',
    bookDom: 'Стакан',
    bookLadder: 'Лесенка',
    bookTape: 'Лента',
    bookDensity: 'плотность',
    bookClickSoon: 'Клик → Limit по этой цене (вкладка Ордер)',
    bookClickHint: 'Клик по цене в стакане / лесенке / ленте подставляет Limit на вкладке Ордер',
    bookArmedLimit: (side: string, price: string) => `Limit ${side} @ ${price} — проверьте Size и отправьте`,
    termExpandPanel: 'Панель ↓',
    termExpandPanelTitle: 'Развернуть позиции / стакан — закрывает график',
    termExpandChart: 'График ↑',
    termExpandChartTitle: 'Развернуть график — закрывает подвал',
    termSplit: 'Вместе',
    termSplitTitle: 'График и подвал рядом',
    termChartCovered: 'График скрыт — разверните сверху',
    termExpand: 'Панель ↓',
    termCollapse: 'Вместе',
    termExpandTitle: 'Развернуть позиции / стакан — закрывает график',
    termCollapseTitle: 'График и подвал рядом',
    termHide: 'График ↑',
    termShow: 'Панель ↓',
    termHideTitle: 'Развернуть график — закрывает подвал',
    termShowTitle: 'Развернуть позиции / стакан — закрывает график',
    loadTape: 'Нажмите «Обновить», чтобы загрузить ленту',
    showPanels: 'Показать панели',
    showPositions: 'Показать позиции ↓',
    lang: 'RU',
    langMenu: 'Язык',
    langEn: 'English',
    langRu: 'Русский',
    strategiesTitle: 'AI-ЧИП',
    strategiesHint: 'Выберите AI-чип (Pulse / Snap / Ladder / Book) → диапазон → риск → «Запустить». Один API-ключ — на разных парах; на одну пару — один живой бот.',
    chipPulseName: 'AI Chip · Pulse',
    chipPulseShort: 'Импульсный чип — читает диапазон и даёт плотные лонг/шорт сетапы',
    chipPulseDesc:
      'AI Chip · Pulse следит за боксом в реальном времени: пробой / откат с каждой стороны. Sketch на графике обновляется сразу; «Запустить» крутит тот же чип в live. Шорт-крутилка по умолчанию жёстче.',
    chipSnapName: 'AI Chip · Snap',
    chipSnapShort: 'Контртрендовый чип — вход на экстремумах SMA/RSI (+ фрактал)',
    chipSnapDesc:
      'AI Chip · Snap ждёт глубокий откат относительно SMA + RSI. Крутилка «больше» ослабляет глубину; крылья фрактала (0=выкл … 5=строго) подтверждают пивоты. Меньше сделок, чем у Pulse — зато на экстремумах.',
    chipLadderName: 'AI Chip · Ladder',
    chipLadderShort: 'Сеточный чип — лесенка усреднения с TP на каждую ногу',
    chipLadderDesc:
      'AI Chip · Ladder строит DCA-лесенку от цены запуска. Лонг шагает вниз, шорт вверх; у каждой ноги свой TP. Те же крутилки деска, что у Pulse / Snap.',
    chipBookName: 'AI Chip · Book',
    chipBookShort: 'Чип ордер-блоков — те же OB-коробки, что на графике',
    chipBookDesc:
      'AI Chip · Book торгует OB на графике: вход по tap/подходу (ближе кирпич побеждает — не дальний wick под красной зоной), стоп = пробой дальней грани, тейк = holdBars / встречный кирпич. Density = строгость + близость.',
    bookWatchEnter: (side: string, lo: string, hi: string, str: string) =>
      `Book · ВХОД ${side} · кирпич ${lo}–${hi} · сила ${str}`,
    bookWatchWait: (side: string, lo: string, hi: string, dist: string, str: string) =>
      `Book · ждём ${side} · кирпич ${lo}–${hi} · ${dist}% до зоны · сила ${str}`,
    bookWatchNone: (fresh: number, tested: number) =>
      `Book · в диапазоне нет кирпича (fresh ${fresh} · tested ${tested}) — нарисуй range / ослабь density`,
    stratRunning: 'Работает',
    stratStopped: 'Остановлена',
    stratStoppedLeft: 'Стоп · позицию оставили',
    stratStoppedClosed: 'Стоп · позицию закрыли',
    stratSoftStopping: 'Мягкий стоп · выход по минпрофиту',
    stopStrategySoftActive: '⏹ Мягкий стоп…',
    strategyStartLine: 'Старт стратегии — от этой линии сравнивайте REAL с sketch',
    posGroupByTab: 'этот таб',
    posGroupByTabTitle: 'Вкл: только позиции / ордера / сделки открытого таба. Выкл: все символы.',
    posGroupByTabEmpty: 'На этом табе нет позиций',
    ordersGroupByTabEmpty: 'На этом табе нет ордеров',
    tradesGroupByTabEmpty: 'На этом табе нет сделок',
    posJumpTab: 'Открыть таб этого символа',
    stratDialNeedStart: 'Крутилки только настраивают — нажми ▶ Start AI Chip (на вкладке должно быть live).',
    stratInPosition: 'В позиции',
    stratSignals: 'Только сигналы',
    stratStatusIdle: 'На этой вкладке AI-чип не запущен',
    stratTabLive: 'live',
    rangeTimeEnding: 'Скоро конец времени стратегии — протяните диапазон вправо',
    rangeTimeEndingLeft: (s: string) => `Окно стратегии кончится через ${s} — потяните правый край`,
    rangeLabel: 'Диапазон',
    rangeNone: 'не задан — весь график',
    rangeTrades: 'Сделки в диапазоне',
    rangeDrawHint: 'Нарисуйте «▣ Диапазон» — на графике появятся сделки выбранного AI-чипа.',
    rangeSketchEmpty: 'Диапазон задан, но этот AI-чип не нашёл sketch-сделок — попробуйте Pulse, ослабьте плотность / фрактал или расширьте бокс.',
    riskPerOrder: 'Риск на один ордер · % депозита',
    riskMax: 'Макс. загрузка AI-чипа · % депозита',
    sideLong: 'Лонг',
    sideShort: 'Шорт',
    tradesFewer: 'меньше',
    tradesMore: 'больше',
    shortStricterHint: 'Шорт по умолчанию жёстче — меньше слабых шортов.',
    fractalWings: 'Крылья фрактала',
    fractalOff: 'выкл',
    fractalWingsHint: '0 = без фрактала · 1–5 = Williams (больше = меньше входов). Влияет на скетч и live.',
    maxClips: 'Макс. клипов (вход + усреднения)',
    onlyProfit: 'Only profit · мин. прибыль %',
    onlyProfitHint: 'Уже в плюсе — ждём минимум net % (после комиссий). Soft-exit в минусе/нуле — закрываем сразу (без залипания). Жёсткий range-стоп это игнорирует.',
    expandCollapse: 'Свернуть ↑',
    monitoringBusy: 'Обновляю…',
    needKeyStrategy: 'Нужен API ключ (вкладка «Кабинет») перед запуском.',
    startStrategy: '▶ Запустить AI Chip',
    stopStrategy: '⏹ Остановить AI Chip',
    sizeWarnMinLot: (minQty: number, notional: number, riskNotional: number, sym: string) =>
      `Риск (~${riskNotional.toFixed(2)} USDT) меньше мин. лота ${sym} (${minQty}) — ордер пойдёт минималкой (~${notional.toFixed(2)} USDT).`,
    sizePreview: (qty: number, notional: number, step: number) =>
      `Клип ≈ ${qty} (шаг ${step}) · ~${notional.toFixed(2)} USDT`,
    delistNoticeTitle: 'Делистинг',
    killDone: (msg: string) => msg,
    undo: '↶ Назад',
    redo: '↷ Вперёд',
    undoTitle: 'Назад (Ctrl+Z) — рисунки, диапазон, пара, ТФ · до 10 шагов',
    redoTitle: 'Вперёд (Ctrl+Shift+Z) · до 10 шагов',
    margin: 'Маржа',
    marginExchange: 'На бирже',
    marginApply: 'Применить на бирже',
    marginAvailable: 'Доступная маржа',
    marginUsed: 'Использованная маржа',
    marginMaint: 'Поддерж. маржа',
    marginEquity: 'Equity',
    marginEffLev: 'Плечо позиций',
    marginRatio: 'Маржин-рейшо',
    cross: 'Cross',
    isolated: 'Isolated',
    leverage: 'Плечо',
    barCountdown: 'Таймер закрытия бара',
    barCountdownHint: 'Обратный отсчёт под ценой на правой шкале — тикает, значит деск живой.',
    indicatorsTitle: 'ИНДИКАТОРЫ НА ГРАФИКЕ',
    indicatorsHintOnly: 'Только подсказки на графике — AI-чипы сами они не запускают и не крутят.',
    indSectionBasics: 'БАЗА',
    indSectionZones: 'ПЕРЕКУП / ПЕРЕПРОД',
    indSectionStructure: 'УРОВНИ · ТРЕНДЫ · ORDER BLOCKS',
    indZonesHint: 'Полосы на панели RSI. Сами по себе это не сигнал входа.',
    indStructureHint: 'Авто-рисунки по свингам — контекст; можно выключить в любой момент.',
    indRsiZones: 'Зоны RSI',
    indRsiOb: 'Перекупленность',
    indRsiOs: 'Перепроданность',
    indAutoLevels: 'Автоуровни',
    indAutoLevelsHint: 'На шкале ×N — сколько раз цена касалась уровня (не номер order block).',
    indLevelsCount: 'Сколько уровней',
    indAutoTrends: 'Автотренды',
    indTrendsCount: 'Линий на сторону',
    indOrderBlocks: 'Order blocks',
    indBlocksCount: 'Сколько',
    indOrderBlocksHint: 'Volumetric OB: свинг→BOS (как LuxAlgo). Метрики справа = объём импульса + доля. Пока включено — опрос стакана.',
    indObBookMode: 'Ширина OB',
    indObBookAnnotate: 'Тело',
    indObBookWiden: 'Стенки',
    indObBookModeHint: 'Тело — узко по свече. Стенки — шире по живому стакану.',
    indObBreakers: 'Breakers',
    indObBreakersCount: 'Сколько breakers',
    indObBreakersHint: 'Пробитые OB со сменой полярности (спрос→сопротивление, предложение→поддержка). Пунктир-призраки.',
    indSensitivity: 'Чувствительность структуры',
    indSensitivityHint: 'Строже ← меньше / сильнее · мягче → больше / слабее. Общая для уровней, трендов, OB и breakers.',
    chipTagPulse: 'Pulse',
    chipTagSnap: 'Snap',
    chipTagLadder: 'Ladder',
    chipTagBook: 'Book',
    posChipCol: 'Chip',
    posChipEmpty: '',
    posChipTitle: 'AI-чип на этом символе (пусто = вручную / без чипа)',
    indSensitivityStrict: 'строго',
    indSensitivityLoose: 'мягко',
    indSaveStructure: 'Сохранить и привязать объекты к таймфрейму',
    indSaveStructureHint: 'Закрепить текущие подсказки на графике с меткой ТФ. Потом стереть ластиком.',
    indSaveStructureDone: (n: number) => `Привязано объектов к ТФ: ${n}`,
    indRsiPane: 'Где RSI',
    indVolPane: 'Где Volume',
    indPaneSeparate: 'Отдельная панель',
    indPaneMain: 'На основном графике',
    indPaneDragHint: 'Тяните чип RSI / Volume: верхняя половина → основной график; нижняя → подвал (подвал скрывается, если оба на основном).',
    indEraseChip: 'ластик · клик выключает',
    sizeModeCoins: 'Монеты',
    sizeModeUsdt: 'USDT',
    sizeModePct: '% депозита',
    sizeHintCoins: 'Размер ордера в монетах базового актива.',
    sizeHintUsdt: 'Номинал в USDT → в монеты по mark / last.',
    sizeHintPct: '% доступной маржи → в монеты по mark / last.',
    sizeInvalid: 'Укажите корректный размер (монеты, USDT или % депозита)',
    bookPrice: 'Цена',
    bookQty: 'Объём',
    bookCum: 'Нараст.',
    themeDark: 'Тёмная',
    themeIvory: 'Слоновая кость',
    themeToggle: 'Тема',
    themeToggleTitle: 'Тёмная / светломолочная (слоновая кость)',
    tgTitle: 'Telegram',
    tgHint: 'Введите @ник — получите ссылку на бота. Алерты и привязка — следующим шагом.',
    tgPlaceholder: '@username',
    tgSave: 'Сохранить ник',
    tgOpenBot: 'Открыть бота',
    tgSaved: (h: string) => `Telegram ${h} сохранён`,
    tgNeedHandle: 'Сначала введите Telegram @username',
    tgBotMissing: 'Имя бота ещё не задано — ник сохранён для будущей привязки.',
    indHistoryScrollHint: 'Крутите график влево — подгрузятся старые бары (≥15m). На 1m / 5m глубже не тянем.',
    indHistoryLoading: 'Гружу старые бары…',
    indHistoryDone: (n: number) => `История · ${n} свечей`,
    indHistoryFail: (m: string) => `Не удалось подгрузить историю: ${m}`,
    noData: 'Нет данных',
    bar: 'бар',
    last: 'last',
    tradesOnChart: 'сделки',
    rangeBox: 'Диапазон стратегии',
    eraseHint: 'удалить',
    object: 'объект',
    draftPoint: 'Точка начала',
    refresh: 'Обновить',
    noKeyShort: 'нет ключа',
    connectKeyPositions: 'Подключите WEEX ключ справа, чтобы видеть позиции',
    connectKeyOrders: 'Подключите ключ, чтобы видеть ордера',
    connectKeyTrades: 'Подключите ключ — здесь будут ваши закрытые сделки (не лента стакана)',
    noPositions: 'Нет открытых позиций',
    posSelectHint: 'Клик по строке или avg на графике → закрыть % · TP/SL',
    posToolsTitle: 'Позиция',
    posClosePct: 'Закрыть %',
    posCloseBtn: 'Закрыть',
    posCloseHint: 'Рынок reduce-only',
    posAttachProtective: 'Поставить',
    posProtectiveHint: 'Plan reduce-only на открытый размер',
    posMoreProtective: 'TP / SL',
    posLessProtective: 'Скрыть',
    posDismiss: 'Закрыть панель',
    posBusy: '…',
    posSize: 'Размер',
    posEntry: 'Вход',
    posUpnl: 'uPnL',
    posQtyClose: 'К закрытию',
    posClosed: (pct: number, sym: string) => `Закрыто ${pct}% ${sym}`,
    posProtectiveAttached: (sym: string) => `TP/SL на ${sym}`,
    posNeedProtective: 'Сначала включите TP, SL или trailing',
    protectiveTitle: 'TP / SL / Trailing',
    protectiveHint: 'Ставятся вместе с входом (reduce-only), как bracket на WEEX.',
    protectiveTp: 'Take profit',
    protectiveSl: 'Stop loss',
    protectiveTrail: 'Trailing',
    protectiveOff: 'Выкл',
    protectivePct: '% от входа',
    protectivePrice: 'Цена',
    protectiveCallback: 'Callback %',
    protectiveActivate: 'Активация (необяз.)',
    protectivePreview: 'Примерно от входа',
    protectiveTpPnl: 'На TP',
    protectiveSlPnl: 'На SL',
    noOrders: 'Нет открытых ордеров',
    noClosedTrades: 'Пока нет реальных сделок по этой паре',
    noBalance: 'Нет данных баланса',
    loadBook: 'Нажмите «Обновить», чтобы загрузить стакан',
    tabAdded: 'Вкладка добавлена',
    tabClosed: 'Вкладка закрыта',
    lastTab: 'Нельзя закрыть последнюю вкладку',
    pairExample: 'Пара: например BTCUSDT',
    badPair: (sym: string) => `Пара «${sym || '?'}» не подходит для WEEX Futures/Spot — выбери другую`,
    pairReset: (sym: string) => `Пара: ${sym} · рынок сброшен (диапазон очищен)`,
    rangeApplied: (r: string) => `Диапазон применён к активной стратегии · ${r}`,
    rangeTab: (r: string) => `Диапазон вкладки: ${r || 'цена'}`,
    rangeClearedLive: 'Диапазон снят · стратегия работает без бокса',
    rangeCleared: 'Диапазон стратегии снят',
    chartSaved: (sym: string) => `График ${sym} сохранён`,
    timeOff: 'Временная рамка выключена — только цена',
    timeRestored: 'Временная рамка восстановлена',
    needKeyCabinet: 'Сначала подключите WEEX API ключ во вкладке «Кабинет»',
    strategyStarted: (k: string, risk: string, max: string, box: string) =>
      `${k} запущен · риск ${risk}%/ордер · макс ${max}%${box}`,
    strategyStopped: 'Стратегия остановлена',
    strategyStoppedLeft: 'Стратегия остановлена · позиция оставлена без присмотра',
    strategyStoppedClosed: 'Стратегия остановлена · позиция закрыта на бирже',
    strategySoftStopped: 'Мягкий стоп · без входов/доборов; закрывает сразу, если минпрофит уже есть (иначе ждёт)',
    strategySoftActive: 'Мягкий стоп — без входов/доборов; маркет-выход как только only-profit пускает (все чипы)',
    stopDialogTitle: 'Остановить AI Chip',
    stopDialogHint: 'Как поступить с открытой позицией и ожидающими входами.',
    stopModeClose: 'Стоп и закрыть позицию',
    stopModeCloseHint: 'Гасим бота и сразу закрываем позицию на бирже (reduce-only).',
    stopModeLeave: 'Стоп, позицию оставить',
    stopModeLeaveHint: 'Бот сразу выключается. Позиция остаётся на бирже без стратегии.',
    stopModeSoft: 'Мягкий стоп (минпрофит → закрыть)',
    stopModeSoftHint: 'Без входов/доборов. Если минпрофит уже есть (или only-profit выкл) — маркет сейчас. Если зелёный, но ниже мин % — ждём. Жёсткий range TP/SL как был. Pulse / Snap / Ladder одинаково.',
    keyConnected: (alias: string) => `Ключ «${alias}» подключён`,
    keyConnectedBal: (alias: string, equity: number, available: number) =>
      `Ключ «${alias}» · equity ${equity.toFixed(2)} · available ${available.toFixed(2)} USDT`,
    keySavedIpBlocked: (alias: string, ip: string) =>
      ip
        ? `Ключ «${alias}» сохранён. Баланс/ордера — только IPv4 ${ip} (графики остаются на IPv6). Проверьте whitelist, обновите деск, «Мониторинг».`
        : `Ключ «${alias}» сохранён. Приватные вызовы WEEX — IPv4, графики — IPv6. Обновите деск и нажмите «Мониторинг».`,
    accountRefreshed: (equity: number, available: number) =>
      `Баланс обновлён · equity ${equity.toFixed(2)} · available ${available.toFixed(2)} USDT`,
    accountRefreshFail: 'Не удалось обновить баланс',
    accountRefreshHint: 'Нажмите «Мониторинг», чтобы подтянуть equity / маржу с WEEX',
    orderSent: (type: string, side: string, qty: string, sym: string) => `${type} ${side} ${qty} ${sym} отправлен`,
    planOn: (id: string) => `План ${id} активирован`,
    marginApplied: (mode: string, lev: string) => `Маржа ${mode} · ${lev}x применена на бирже`,
    fewCandles: (sym: string, tf: string) => `Мало свечей для ${sym} ${tf}`,
    loadingTf: 'Загрузка свечей…',
    netStable: 'Рынок стабилен',
    netWeak: 'Рынок слабый — повтор…',
    netOffline: 'Рынок офлайн',
    netConnecting: 'Рынок подключается…',
    accountOk: 'счёт OK',
    accountEmpty: 'баланс 0 / проверьте ключ',
    accountFail: 'ошибка счёта',
    personalCabinet: 'ЛИЧНЫЙ КАБИНЕТ',
    tabLabel: 'Вкладка',
    keyOk: 'ключ OK',
    noKey: 'нет ключа',
    positionsCount: 'Позиций',
    perMonth: '/мес',
    freePlan: 'без оплаты',
    monitoring: 'Мониторинг',
    monitoringOpened: 'Мониторинг открыт · баланс и позиции обновлены',
    monitorEquityChart: 'Equity фьючерсов',
    monitorSpotBalance: 'Spot USDT',
    monitorPnlChart: 'Реализ. PnL',
    monitorDeposit: 'Депозит',
    monitorChartsEmpty: 'Ждём точки…',
    monitorChartsHint: 'Spot и Futures — разные кошельки; у каждого свой график equity на этом устройстве',
    monitorMarketFutures: 'Futures',
    monitorMarketSpot: 'Spot',
    monitorEquitySeparate: 'свой кошелёк · свой график',
    monitorSpotAvailable: 'Доступно',
    monitorAvailShort: 'доступно',
    monitorChartFor: 'График equity',
    monitorSwitchMarketHint: 'Переключить рынок вкладки — графики Spot и Futures хранятся отдельно',
    monitorPeriod15m: '15м',
    monitorPeriod1h: '1ч',
    monitorPeriod6h: '6ч',
    monitorPeriod24h: '24ч',
    monitorPeriodAll: 'Всё',
    monitorMeta: (pts: number, period: string) => `${pts} тчк · ${period}`,
    monitorPollEvery: 'каждые 12 с',
    expand: 'Развернуть ↓',
    apiKeysOne: 'API ключи (общие для вкладок)',
    keyShareHint: 'Один ключ работает на всех вкладках. Стратегии на разных парах — с тем же ключом; на одну пару — один живой бот. Маржа ниже — по всем позициям ключа.',
    liveBotsOther: 'Другие живые боты на этом ключе',
    noKeyOption: '— без ключа —',
    keyNotConnected: 'Ключ ещё не подключён',
    addKey: '+ Добавить / заменить ключ',
    connectKey: 'Подключить API ключ',
    deleteKey: 'Удалить',
    deleteKeyConfirm: (alias: string) => `Удалить ключ «${alias}» из Termix? На бирже ничего не меняется.`,
    keyDeleted: (alias: string) => `Ключ «${alias}» удалён`,
    keySaving: 'Сохраняю…',
    keySavedList: 'Сохранённые ключи — выберите для вкладки или удалите',
    keyReplaceHint: 'То же имя (например main) заменяет этот ключ. Секреты больше не показываем — только удалить и добавить заново.',
    cancel: 'Отмена',
    save: 'Сохранить',
    billingHint: 'Планы Free / Pro $10 / Desk $20 — Aptos USDT в Кабинете. Или скидка 50% по объёму (≥$150k / ≥$300k).',
    account: 'Аккаунт',
    depositWallet: 'Депозитный кошелёк Aptos',
    depositWalletPending: 'Кошелёк ещё не готов — Admin → «Установить» (миграция billing), потом откройте окно снова',
    depositWalletHint: 'Шлите нативный Aptos USDT сюда для Pro/Desk. Комиссия платформы уходит на treasury при активации.',
    changePassword: 'Сменить пароль',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    savePassword: 'Сохранить пароль',
    passwordChanged: 'Пароль обновлён',
    tabLimitPay: 'Лимит вкладок — нужна оплата плана',
    upgradeTitle: 'Оплата плана',
    upgradeHint: (plan: string, amount: number) => `План ${plan}: переведите ${amount} USDT (Aptos) на депозитный адрес.`,
    platformFee: 'Комиссия платформы',
    iPaid: 'Проверить и активировать',
    closedTradesHint: 'REAL-сделки остаются на паре даже после снятия диапазона',
    saveWithChart: 'Сохранить с графиком',
    on: 'вкл',
    helpTitle: 'Termix · база знаний',
    helpBack: '← к терминалу',
    helpBackLogin: '← ко входу / регистрации',
    helpVideoTitle: 'Видео-обзор · ~3 мин',
    helpVideoLead: 'Полный обзор: тема и язык · Панель/График · стакан→Limit · OB тело/стенки · уровни · Pulse/Snap Range · ластик. Курсор и подписи.',
    authTagline: 'WEEX desk · AI Chip · Pulse / Snap / Ladder',
    authHelpLink: 'Обзор деска (~3 мин):',
    authPlansLine: 'Free 2 · Pro $10 / 5 вкладок · Desk $20 / 10 вкладок',
    authEmail: 'Email',
    authPassword: 'Пароль',
    authPasswordPh: 'минимум 8 символов',
    authLogin: 'Войти',
    authRegister: 'Создать аккаунт',
    authSwitchRegister: 'Нет аккаунта — регистрация',
    authSwitchLogin: 'Уже есть аккаунт — вход',
    authAdminLink: 'Вход в Admin →',
    authBadCreds: 'Неверный email или пароль. Админ-панель: /admin. Новый аккаунт — «регистрация».',
    authWeexAsk: 'Есть аккаунт на WEEX?',
    authWeexYes: 'Да',
    authWeexNo: 'Пока нет',
    authWeexRefLead: 'Зарегистрируйтесь на WEEX по нашей ссылке — кэшбек 10% на все комиссии:',
    authWeexRefCta: 'Открыть регистрацию WEEX →',
    weexRefUrl: 'https://www.weex.com/ru/register?vipCode=myway1',
    helpQuickTitle: 'Быстрый старт',
    helpQuick: [
      'Открой пару и ТФ (15m удобен для Pulse).',
      'Нарисуй **Range** — зону работы AI-чипа.',
      'Справа → **AI-чип** → **AI Chip · Pulse**.',
      'Задай риск % → Кабинет → API-ключ → **Start strategy**.',
    ],
    helpStratTitle: 'AI-чипы · главный фокус Termix',
    helpStratLead: 'Три AI-чипа крутятся на вкладке с вашим API-ключом. Рисуете диапазон → выбираете Pulse / Snap / Ladder → риск % → Start. На графике сразу виден sketch сделок выбранного чипа.',
    helpVolTitle: 'AI Chip · Pulse ★',
    helpVolBody: 'Импульсный чип: breakout за lookback high/low или pullback к середине диапазона. На активном рынке даёт **много чистых сделок** внутри бокса — маркеры L / S / xL / xS сразу на свечах. Выход режется **only profit** (не закрываем в минус после комиссий). Основной «рабочий» чип деска.',
    helpHideepTitle: 'AI Chip · Snap',
    helpHideepBody: 'Контртрендовый чип: глубокий откат от SMA + RSI. Ползунок фрактала 0–5 (0=выкл). Меньше сделок, чем у Pulse — зато на экстремумах.',
    helpDcaTitle: 'AI Chip · Ladder',
    helpDcaBody: 'Сеточный чип: лесенка усреднения с TP на каждую ногу. Держится внутри trade range.',
    helpStratFoot: 'Смена чипа пишется во вкладку сама; кнопка Saved становится dirty, если ещё не сохранили чертёж.',
    helpRangeTitle: 'Диапазон и сделки на графике',
    helpRangeItems: [
      '**Range** — два клика: верх/низ. Галочка **by time** стоит рядом с ▣ Range в одной рамке (потом Trend и Eraser) — привязка по времени именно к боксу; можно тянуть в будущее.',
      'Пока бокс есть — стратегия и набросок сделок работают **только внутри** него.',
      '**Сделки в диапазоне** — маркеры выбранного алгоритма на истории (превью до запуска).',
      'Ластик по боксу снимает диапазон со вкладки; без бокса стратегия смотрит весь график.',
    ],
    helpChartTitle: 'График и таймфреймы',
    helpChartBody: 'Кнопки **15m / 30m / 1h / 4h** меняют интервал на текущей вкладке. Вкладка сверху показывает пару и ТФ вместе.',
    helpChartPair: '**Сменить пару** сбрасывает старый ценовой бокс — цены другой монеты ломают шкалу.',
    helpDrawTitle: 'Рисование',
    helpDrawItems: [
      '**Курсор** — просмотр, перетаскивание точек тренда и краёв диапазона.',
      '**H-линия** — уровень по клику; тяните ручку.',
      '**Тренд** — два клика, потом круглые точки.',
      '**Ластик / Delete** — снять рисунок или выключить индикатор (SMA / RSI / уровни / OB…). Undo / Redo в панели чертежа.',
      '**Чипы RSI / Volume** — перетащите на основной график или оставьте отдельной панелью (как в TradingView).',
    ],
    helpRiskTitle: 'Риск, TP / SL / Trailing',
    helpRiskBody1: '**Риск % на ордер** — доля депозита на один вход. **Макс. % стратегии** — потолок суммарной загрузки.',
    helpRiskBody2: 'Вкладка Order → **Protective** на новый вход. Открытая позиция: клик по строке или линии/бейджу **avg** на графике → ползунок закрыть % + TP/SL/trailing на живой размер (reduce-only).',
    helpRiskBody3: '**Stop strategies** гасит ботов на всех вкладках. Позиции на бирже сама кнопка не закрывает — для этого Position tools.',
    helpBottomTitle: 'Низ панели',
    helpBottomItems: [
      '**Позиции / Ордера** — по ключу этой вкладки.',
      '**Сделки** — ваши закрытые (не лента стакана).',
      '**Стакан / Balance** — книга заявок и маржа с биржи.',
    ],
    helpAdminTitle: 'Админка · обновления',
    helpAdminBody: '«Проверить» / «Установить» смотрят на **GitHub main** репозитория',
    helpAdminFoot: 'Если статус «актуально», а на сайте старое — Attach git + Install (ключ /root/.ssh/termix_deploy).',
  },
} as const;

export type Dict = typeof dict.en;

/** Localized name / short / description for desk AI Chip cards (catalog stays English for engine). */
export const chipCopy = (
  t: Dict,
  kind: StrategyKind | string | null | undefined,
): { name: string; short: string; description: string } => {
  switch (kind) {
    case 'hideep':
      return { name: t.chipSnapName, short: t.chipSnapShort, description: t.chipSnapDesc };
    case 'redflag_dca':
      return { name: t.chipLadderName, short: t.chipLadderShort, description: t.chipLadderDesc };
    case 'order_book':
      return { name: t.chipBookName, short: t.chipBookShort, description: t.chipBookDesc };
    case 'volume_churn':
    default:
      return { name: t.chipPulseName, short: t.chipPulseShort, description: t.chipPulseDesc };
  }
};

/** Tiny tab / positions badge — Pulse · Snap · Ladder · Book. */
export const chipTabTag = (
  t: Dict,
  kind: StrategyKind | string | null | undefined,
): string => {
  switch (kind) {
    case 'hideep': return t.chipTagSnap;
    case 'redflag_dca': return t.chipTagLadder;
    case 'order_book': return t.chipTagBook;
    case 'volume_churn': return t.chipTagPulse;
    default: return kind ? String(kind) : '';
  }
};

type I18nCtx = { lang: Lang; setLang: (l: Lang) => void; t: Dict };

const Ctx = createContext<I18nCtx | null>(null);

const STORAGE_KEY = 'termix_lang';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'ru' || v === 'en') return v;
    } catch { /* ignore */ }
    return 'en';
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  };
  const value = useMemo(() => ({ lang, setLang, t: dict[lang] as Dict }), [lang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = (): I18nCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside LanguageProvider');
  return v;
};
